import { computed, Injectable, signal } from '@angular/core';
import { UploadSessionItem } from '@modules/reconciliation-manager/models';
import { AuthUser as OidcUser } from '@modules/auth/models/user-oidc';

import { initialState, UserDataInfo } from '../models/auth-store.model';
import { isPlatformMode, PlatformMode } from '../models/platform-mode.model';

@Injectable({ providedIn: 'root' })
export class AuthStore {
    private _userInfoData = signal<UserDataInfo>({ ...initialState });

    /** Expose state as readonly signal */
    readonly userInfoData = this._userInfoData.asReadonly();

    private readonly STORAGE_KEY = 'auth_user_data';


    /** Derived login flag */
    readonly isLoggedIn = computed(() => {
        const { accessToken, tokenExpiry } = this._userInfoData();
        return !!accessToken && !!tokenExpiry && tokenExpiry > Date.now();
    });

    private _uploadSessionsData = signal<UploadSessionItem[]>([]);

    private _currentClientId = signal<string | null>(null);
    private _currentUserId = signal<string | null>(null);
    readonly currentClientId = this._currentClientId.asReadonly();
    readonly currentUserId = this._currentUserId.asReadonly();

    constructor() {
        this.restoreFromStorage();
    }

    getClientValue(): string | null {
        return this._currentClientId();
    }
    getUserValue(): string | null {
        return this._currentUserId();
    }

    getUserDisplayValue(): string | null {
        const { firstName, lastName, email } = this._userInfoData();
        const name = [firstName, lastName].filter(Boolean).join(' ');
        return name || email || null;
    }

    setUploadSessionData(data: UploadSessionItem[]) {
        this._uploadSessionsData.set(data);
    }

    getUploadSessionData(): UploadSessionItem[] {
        return this._uploadSessionsData();
    }

    setSelectedClient(id: string | null): void {
        this._currentClientId.set(id);
    }
    setSelectedUser(id: string | null): void {
        this._currentUserId.set(id);
    }

    private mapOidcUser(user: OidcUser): void {
        const profile = (user.profile as any) || {};
        const rawRoles = profile.role ?? profile.roles ?? [];
        const currentState = this._userInfoData();
        const normalize = (value: unknown): string | null =>
            typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
        const userId = normalize(profile.sub);
        const sameSubject = !!userId && currentState.userId === userId;
        const rawTenantId = normalize(profile.tenant_id);
        const clientId = rawTenantId?.toLowerCase() === 'system'
            ? null
            : rawTenantId || (sameSubject ? currentState.clientId : null);
        const incomingRoles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
            .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
            .map((role) => role.trim());
        const userData: UserDataInfo = {
            user,
            userId,
            username:
                normalize(profile.preferred_username) ||
                normalize(profile.name) ||
                normalize(profile.email) ||
                (sameSubject ? currentState.username : null),
            clientId,
            subject: userId || (sameSubject ? currentState.subject : null),
            clientName: normalize(profile.client_name) || (sameSubject ? currentState.clientName : null),
            clientDisplayName:
                normalize(profile.client_display_name) ||
                (sameSubject ? currentState.clientDisplayName : null),
            clientSlug: normalize(profile.client_slug) || (sameSubject ? currentState.clientSlug : null),
            clientLogo: normalize(profile.client_logo) || (sameSubject ? currentState.clientLogo : null),
            clientColor: normalize(profile.client_color) || (sameSubject ? currentState.clientColor : null),
            firstName: normalize(profile.given_name) || (sameSubject ? currentState.firstName : null),
            lastName: normalize(profile.family_name) || (sameSubject ? currentState.lastName : null),
            email: normalize(profile.email) || (sameSubject ? currentState.email : null),
            platformMode: sameSubject ? currentState.platformMode : null,
            roles: incomingRoles.length > 0 || !sameSubject ? incomingRoles : currentState.roles,
            accessToken: user.access_token ?? null,
            refreshToken: (user as any).refresh_token ?? null,
            tokenExpiry: user.expires_at ? user.expires_at * 1000 : null,
            dateLoggedIn: Date.now(),
            isLoading: false,
            error: null,
        };

        this._userInfoData.set(userData);
        this.saveToStorage(userData);

        this._currentClientId.set(clientId);
        this._currentUserId.set(userId);
    }

    public updateUserContext(ctx: { userId: string; clientId: string; userData: OidcUser }): void {
        this.mapOidcUser(ctx.userData);
    }

    /** Persist platform_mode from AuthContext so guards do not depend on route-time API calls. */
    public setPlatformMode(platformMode: PlatformMode | null): void {
        const userData = {
            ...this._userInfoData(),
            platformMode,
        };

        this._userInfoData.set(userData);
        this.saveToStorage(userData);
    }

    public getPlatformMode(): PlatformMode | null {
        return this._userInfoData().platformMode ?? null;
    }

    public clearUserContext(): void {
        this._userInfoData.set({ ...initialState });
        this._currentClientId.set(null);
        this._currentUserId.set(null);
        this.clearStorage();
    }

    /** Reset to initial empty state */
    clearAuthData(): void {
        this._userInfoData.set({ ...initialState });
        this._currentClientId.set(null);
        this._currentUserId.set(null);
        this.clearStorage();
    }

    getUserId(): string | null {
        /* return this. get User Value ();*/
        return this._currentUserId();
    }

    getClientId(): string | null {
        return this.getClientValue();
    }

    getUsername(): string | null {
        return this._userInfoData().username ?? null;
    }

    getClientName(): string | null {
        return this._userInfoData().clientName ?? null;
    }

    getSubject(): string | null {
        return this._userInfoData().subject;
    }

    /** Readable first name */
    getUserFirstName(): string | null {
        return this._userInfoData().firstName;
    }

    /** Readable full name (optional) */
    getUserFullName(): string {
        const { firstName, lastName } = this._userInfoData();
        if (firstName || lastName) {
            return `${firstName ?? ''} ${lastName ?? ''}`.trim();
        }
        return 'Unknown';
    }

    getClientDisplayName(): string | null {
        return this._userInfoData().clientDisplayName;
    }

    getClientSlug(): string | null {
        return this._userInfoData().clientSlug;
    }

    getClientLogo(): string | null {
        return this._userInfoData().clientLogo;
    }

    getClientColor(): string | null {
        return this._userInfoData().clientColor;
    }

    private saveToStorage(userData: UserDataInfo): void {
        try {
            const toStore = {
                userId: userData.userId,
                username: userData.username,
                clientId: userData.clientId,
                subject: userData.subject,
                clientName: userData.clientName,
                clientDisplayName: userData.clientDisplayName,
                clientSlug: userData.clientSlug,
                clientLogo: userData.clientLogo,
                clientColor: userData.clientColor,
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                platformMode: userData.platformMode,
                roles: userData.roles,
                tokenExpiry: userData.tokenExpiry,
                dateLoggedIn: userData.dateLoggedIn,
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(toStore));
        } catch {
            // Local persistence is best effort; authentication state remains in memory.
        }
    }

    private restoreFromStorage(): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (!stored) return;

            const data = JSON.parse(stored);

            if (data.tokenExpiry && data.tokenExpiry < Date.now()) {
                this.clearStorage();
                return;
            }

            this._userInfoData.set({
                user: null,
                userId: data.userId ?? null,
                username: data.username ?? null,
                clientId: data.clientId ?? null,
                subject: data.subject ?? null,
                clientName: data.clientName ?? null,
                clientDisplayName: data.clientDisplayName ?? null,
                clientSlug: data.clientSlug ?? null,
                clientLogo: data.clientLogo ?? null,
                clientColor: data.clientColor ?? null,
                firstName: data.firstName ?? null,
                lastName: data.lastName ?? null,
                email: data.email ?? null,
                platformMode: this.normalizePlatformMode(data.platformMode),
                roles: data.roles ?? [],
                accessToken: null,
                refreshToken: null,
                tokenExpiry: data.tokenExpiry ?? null,
                dateLoggedIn: data.dateLoggedIn ?? null,
                isLoading: false,
                error: null,
            });

            this._currentClientId.set(data.clientId ?? null);
            this._currentUserId.set(data.userId ?? null);
        } catch {
            this.clearStorage();
        }
    }

    private clearStorage(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch {
            // Local persistence is best effort.
        }
    }

    private normalizePlatformMode(platformMode: unknown): PlatformMode | null {
        return isPlatformMode(platformMode) ? platformMode : null;
    }
}
